// IdleLogoutRefactor.jsx
import React, { useEffect, useRef, useState } from "react";
import { Modal } from "antd";
import { useIdleTimer } from "react-idle-timer";
import { useDispatch } from "react-redux";
import session_time from "../../assets/icons/session_time.png";
import timeIcon from "../../assets/icons/time.png";
import { logout } from "../../actions/auth";
import usePayAxios from "../../config/useAxios";
import { jwtDecode } from "jwt-decode"; // default import is safer

// const SESSION_MS = 15 * 60 * 1000; // 15 minutes in ms
// const WARNING_MS = 30 * 1000; // show warning 30s before expiry
// const IDLE_TIMEOUT = SESSION_MS - WARNING_MS; // idle-timer timeout
const REFRESH_THRESHOLD_MS = 60 * 1000; // refresh if token expires within 60s

const SESSION_MS = 13 * 60 * 1000; // 1 min
const WARNING_MS = 30 * 1000; // 10s
const IDLE_TIMEOUT = SESSION_MS - WARNING_MS; // 50s

const IdleLogout = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [countdown, setCountdown] = useState(WARNING_MS / 1000); // seconds
  const countdownRef = useRef(null);
  const mounted = useRef(true);

  const dispatch = useDispatch();

  // keep your hook usage - it returns onCall and loading
  const { onCall: refreshApi, loading } = usePayAxios({
    api: "/api/auth/v1/refreshToken",
    method: "post",
    skipSuccessMessage: true,
  });

  // helper: read expiry from sessionStorage safely (returns epoch seconds or null)
  const getExpirySec = () => {
    const e = sessionStorage.getItem("expiresAt");
    return e ? Number(e) : null;
  };

  // helper: attempt refresh token call, return true if refreshed
  const tryRefreshToken = async () => {
    try {
      const user_id = sessionStorage.getItem("user_id");
      const company_id = sessionStorage.getItem("company_id");
      if (!user_id || !company_id) return false;

      if (loading) return false; // avoid concurrent refreshes

      const res = await refreshApi({
        data: { user_id, company_id },
      });

      const refreshToken = res?.response?.refresh_token;
      if (refreshToken) {
        try {
          const decoded = jwtDecode(refreshToken);
          if (decoded?.exp) {
            sessionStorage.setItem("expiresAt", decoded.exp);
            sessionStorage.setItem("token", refreshToken);
            return true;
          }
        } catch (err) {
          // decode failed: still set token if present (optional)
          sessionStorage.setItem("token", refreshToken);
          return true;
        }
      }
    } catch (err) {
      // optional: log or handle refresh failure
      // console.error("refresh failed", err);
      doLogout();
    }
    return false;
  };

  // Called when user becomes idle (we show modal and start countdown)
  const handleOnIdle = () => {
    // Only show modal if there is an existing token
    const token = sessionStorage.getItem("token");
    if (!token) return;

    setCountdown(WARNING_MS / 1000);
    setModalVisible(true);

    // Pause the idle timer (react-idle-timer handles this when using pause())
    // Start single countdown interval
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        // If token expired already -> logout immediately
        const expirySec = getExpirySec();
        const nowMs = Date.now();
        if (expirySec && nowMs > expirySec * 1000) {
          // token expired -> logout
          doLogout();
          return WARNING_MS / 1000;
        }

        if (prev <= 1) {
          // countdown reached zero
          doLogout();
          return WARNING_MS / 1000;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Centralized logout that cleans up
  const doLogout = () => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setModalVisible(false);
    setCountdown(WARNING_MS / 1000);
    dispatch(logout());
  };

  // onAction: whenever user interacts, check if token needs refreshing
  const handleOnAction = async () => {
    const expirySec = getExpirySec();
    if (!expirySec) return;

    const nowMs = Date.now();
    const timeLeftMs = expirySec * 1000 - nowMs;
    if (timeLeftMs <= REFRESH_THRESHOLD_MS) {
      // token will expire soon -> refresh
      await tryRefreshToken();
    }
  };

  // Keep react-idle-timer setup
  const { start, reset, pause } = useIdleTimer({
    onIdle: () => {
      // only open modal when token exists
      const token = sessionStorage.getItem("token");
      if (token) handleOnIdle();
    },
    onAction: handleOnAction,
    timeout: IDLE_TIMEOUT,
    throttle: 500,
    crossTab: true,
  });

  // Keep-alive button: user confirms to stay signed in
  const keepSignedIn = async () => {
    setModalVisible(false);
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    reset(); // restart idle detection
    // if token is near expiry, refresh now
    const expirySec = getExpirySec();
    const nowMs = Date.now();
    if (expirySec && nowMs > expirySec * 1000 - 30000) {
      // if within 30s of expiry, attempt refresh
      await tryRefreshToken();
    }
  };

  useEffect(() => {
    // start idle timer on mount
    start();

    return () => {
      mounted.current = false;
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, []); // run once

  return (
    <Modal
      visible={modalVisible}
      title={
        <div className="flex-title">
          <img src={timeIcon} width={18} alt="time" /> Session Timeout
        </div>
      }
      onCancel={() => {
        setModalVisible(false);
        reset();
        if (countdownRef.current) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
        }
        setCountdown(WARNING_MS / 1000);
      }}
      footer={[]}
      maskClosable={false}
      keyboard={false}
    >
      <div className="session-timeout">
        <div className="content">
          <div>
            <label>Your session is about to expire!</label>
            <span>
              You will be logged out in <b>{countdown}s</b>
            </span>
            <span>Do you want to stay signed in?</span>
          </div>
          <div>
            <img src={session_time} alt="session" />
          </div>
        </div>
        <div className="action-btns mt1">
          <button className="more" onClick={keepSignedIn}>
            Keep me signed in
          </button>
          <button className="primaryButton" onClick={doLogout}>
            Logout
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default IdleLogout;
